import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { useGetPets } from "../../hooks/queries/useGetPets";
import { useGetPetTransactions } from "../../hooks/queries/useGetPetTransactions";
import { useAdjustPetCoins } from "../../hooks/updateSets/useAdjustPetCoins";
import { cn } from "../../lib/utils";
import { stafferPets } from "../../lib/stafferPets";
import type { Pet } from "../../types";

const adjustmentSchema = z.object({
  amount: z.coerce.number().int().min(1).max(1000000),
  description: z.string().trim().min(1, "Enter a reason").max(300),
});

export const StafferPetAdminCoins = ({
  pet,
  isDisabled,
  onSaved,
}: {
  pet: Pet;
  isDisabled: boolean;
  onSaved: (pet: Pet) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { refetch } = useGetPets();
  // Update
  const { mutateAsync, isPending } = useAdjustPetCoins();

  /* React Hooks */
  const [operation, setOperation] = useState<"GRANT" | "REFUND">("GRANT");
  const [isOpen, setIsOpen] = useState(false);
  const [purchaseId, setPurchaseId] = useState<number | null>(null);
  const {
    data: transactions = [],
    isPending: isLoadingPurchases,
    isError: hasPurchaseError,
  } = useGetPetTransactions(operation === "REFUND" ? pet.userId : undefined);
  const [message, setMessage] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const isSubmitting = useRef(false);
  const request = useRef<{ signature: string; id: string } | null>(null);
  const form = useForm<z.infer<typeof adjustmentSchema>>({
    resolver: zodResolver(adjustmentSchema),
    defaultValues: { amount: 25, description: "" },
  });

  /* Handlers */
  const handleSubmit = async (values: z.infer<typeof adjustmentSchema>) => {
    if (
      isSubmitting.current ||
      isDisabled ||
      (operation === "REFUND" && purchaseId === null)
    )
      return;
    isSubmitting.current = true;
    setIsChecking(true);
    setMessage("");
    try {
      const refreshed = await refetch();
      if (refreshed.isError) throw refreshed.error;
      const latest = refreshed.data?.find(
        (entry) => entry.userId === pet.userId,
      );
      if (!latest?.updatedAt || latest.updatedAt !== pet.updatedAt) {
        setMessage(
          "This pet changed. Review its latest balance, then try again.",
        );
        if (latest) onSaved(latest);
        return;
      }
      const signature = JSON.stringify([pet.userId, operation, values]);
      const purchase = transactions.find(
        (entry) => Number(entry.id) === purchaseId,
      );
      const purchasedNames =
        purchase?.description.replace(/^Unlocked /, "").split(", ") || [];
      const refundUnlocks = {
        petIds: stafferPets.AVAILABLE_SPECIES.filter((item) =>
          purchasedNames.includes(`${item.label} pet`),
        ).map((item) => item.id),
        colorIds: Object.entries(stafferPets.PALETTES)
          .filter(([, item]) => purchasedNames.includes(`${item.label} color`))
          .map(([id]) => id),
        topperIds: Object.entries(stafferPets.TOPPERS)
          .filter(([, item]) => purchasedNames.includes(item.label))
          .map(([id]) => id),
        accessoryIds: Object.entries(stafferPets.ACCESSORIES)
          .filter(([, item]) => purchasedNames.includes(item.label))
          .map(([id]) => id),
      };
      if (request.current?.signature !== signature) {
        request.current = { signature, id: crypto.randomUUID() };
      }
      const saved = await mutateAsync({
        userId: pet.userId,
        ...values,
        operation,
        expectedUpdatedAt: latest.updatedAt,
        requestId: request.current.id,
        ...(operation === "REFUND" && { refundUnlocks }),
        ...(operation === "REFUND" &&
          purchaseId !== null && { purchaseTransactionId: purchaseId }),
      });
      onSaved(saved);
      form.reset();
      setPurchaseId(null);
      request.current = null;
      setMessage(
        `${values.amount} coins ${operation === "REFUND" ? "refunded" : "added"}.`,
      );
    } catch {
      setMessage(
        "Could not confirm the adjustment. Refresh and check the ledger before trying again.",
      );
    } finally {
      isSubmitting.current = false;
      setIsChecking(false);
    }
  };

  /* Render */
  const renderPurchases = (isBusy: boolean) => {
    if (operation !== "REFUND") return null;
    if (isLoadingPurchases)
      return <Text className="font-mono text-xs">Loading purchases…</Text>;
    if (hasPurchaseError)
      return (
        <Text className="font-mono text-xs text-red-700">
          Could not load purchases. Reopen Refund to try again.
        </Text>
      );
    const refundedIds = new Set(transactions.map((entry) => entry.eventId));
    const purchases = transactions
      .filter((entry) => entry.type === "PURCHASE" && entry.amount < 0)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, 10);
    if (!purchases.length)
      return <Text className="font-mono text-xs">No recent purchases.</Text>;
    return (
      <Column className="max-h-48 gap-2 overflow-y-auto">
        {purchases.map((purchase) => {
          const isRefunded = refundedIds.has(`admin-refund:${purchase.id}`);
          return (
            <Button
              key={purchase.id}
              type="button"
              variant="outlineDark"
              disabled={isBusy || isRefunded}
              onClick={() => {
                setPurchaseId(Number(purchase.id));
                form.setValue("amount", -purchase.amount);
                form.setValue(
                  "description",
                  `Purchase #${purchase.id}: ${purchase.description}`.slice(
                    0,
                    300,
                  ),
                );
                setMessage("");
              }}
              className={cn(
                "h-auto justify-between gap-3 whitespace-normal rounded-none border-2 border-slate-900 bg-white p-2 text-left font-mono text-xs",
                purchaseId === Number(purchase.id) && "bg-amber-200",
              )}
            >
              <Column className="min-w-0 gap-1">
                <Text className="text-xs">{purchase.description}</Text>
                <Text className="text-[10px] text-slate-500">
                  {new Date(purchase.createdAt).toLocaleDateString()}
                </Text>
              </Column>
              <Text className="shrink-0 text-xs font-bold">
                {isRefunded ? "Refunded" : `${-purchase.amount} coins`}
              </Text>
            </Button>
          );
        })}
      </Column>
    );
  };

  const render = () => {
    const isBusy = isPending || isChecking || isDisabled;
    let submitLabel = "Apply credit";
    if (operation === "REFUND") submitLabel = "Confirm refund";
    if (isChecking) submitLabel = "Saving…";
    return (
      <>
        <Row className="items-center gap-2">
          <Text className="mr-1 font-mono text-xs font-black text-slate-600">
            {pet.coinBalance.toLocaleString()} coins
          </Text>
          {(["GRANT", "REFUND"] as const).map((option) => (
            <Button
              key={option}
              type="button"
              variant="outlineDark"
              disabled={isBusy}
              onClick={() => {
                setOperation(option);
                setPurchaseId(null);
                form.reset();
                setMessage("");
                setIsOpen(true);
              }}
              className={cn(
                "rounded-none border-2 border-slate-900 font-mono text-xs",
              )}
            >
              {option === "GRANT" ? "+ Coins" : "Refund"}
            </Button>
          ))}
        </Row>
        <Dialog
          open={isOpen}
          onOpenChange={(open) => {
            if (!isChecking && !isPending) setIsOpen(open);
          }}
        >
          <DialogContent className="max-h-[80vh] w-[calc(100%_-_2rem)] max-w-sm gap-3 overflow-y-auto rounded-none border-4 border-slate-900 bg-amber-50 p-4 shadow-[6px_6px_0_#172033] sm:rounded-none">
            <DialogHeader>
              <DialogTitle className="font-mono text-sm font-black uppercase">
                {operation === "GRANT" ? "Add coins" : "Refund purchase"}
              </DialogTitle>
              <DialogDescription className="font-mono text-[10px] text-slate-600">
                {operation === "GRANT"
                  ? "Adds coins and lifetime earnings toward incentives."
                  : "Refunds coins, removes purchased unlocks, and unequips them. Incentive progress stays unchanged."}
              </DialogDescription>
            </DialogHeader>
            {renderPurchases(isBusy)}
            <Form {...form}>
              <form
                onSubmit={(event) => {
                  void form.handleSubmit(handleSubmit)(event);
                }}
              >
                <Row className="flex-wrap items-start gap-3">
                  {operation === "GRANT" ? (
                    <>
                      <FormField
                        control={form.control}
                        name="amount"
                        render={({ field }) => (
                          <FormItem className="w-28">
                            <FormLabel>Coins</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="number"
                                min={1}
                                max={1000000}
                              disabled={isBusy}
                                className="rounded-none bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem className="min-w-40 flex-1">
                            <FormLabel>Reason</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                maxLength={300}
                              disabled={isBusy}
                                placeholder="Purchase refund or bonus reason"
                                className="rounded-none bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  ) : null}
                  {operation === "REFUND" && purchaseId !== null ? (
                    <Text className="w-full font-mono text-xs font-bold text-slate-800">
                      Return {form.getValues("amount")} coins and remove the
                      purchased items.
                    </Text>
                  ) : null}
                  <Button
                    type="submit"
                    disabled={
                      isBusy || (operation === "REFUND" && purchaseId === null)
                    }
                    className="w-full rounded-none font-mono text-xs"
                  >
                    {submitLabel}
                  </Button>
                </Row>
              </form>
            </Form>
            {message ? (
              <Text className="font-mono text-xs text-slate-700">
                {message}
              </Text>
            ) : null}
          </DialogContent>
        </Dialog>
      </>
    );
  };
  return render();
};
